import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Reusable searchable combobox (dropdown with search).
 * Solves the scroll issue with Popover + Command by using modal={true}
 * and stopping wheel/touch propagation.
 *
 * Props:
 *   value        - Current selected value (string)
 *   onSelect     - (value: string) => void
 *   options      - Array of { value: string, label: string, searchValue?: string }
 *   placeholder  - Placeholder text when nothing selected
 *   searchPlaceholder - Placeholder for the search input
 *   emptyText    - Text when no results found
 *   disabled     - boolean
 *   className    - Additional classes for the trigger button
 *   contentClassName - Additional classes for the popover content
 *   "data-testid" - Test ID for the trigger button
 */
const SearchableCombobox = ({
  value,
  onSelect,
  options = [],
  placeholder = "Seleccionar...",
  searchPlaceholder = "Buscar...",
  emptyText = "No se encontraron resultados.",
  disabled = false,
  className,
  contentClassName,
  ...props
}) => {
  const [open, setOpen] = useState(false);

  const selectedOption = options.find(
    (opt) => opt.value === value || opt.label?.toLowerCase() === value?.toLowerCase()
  );

  const handleSelect = (optValue) => {
    onSelect(optValue);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal",
            !value && "text-muted-foreground",
            className
          )}
          data-testid={props["data-testid"]}
        >
          <span className="truncate">
            {selectedOption?.label || value || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn("w-[--radix-popover-trigger-width] p-0", contentClassName)}
        align="start"
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.searchValue || opt.label}
                  onSelect={() => handleSelect(opt.value)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      (value === opt.value || value?.toLowerCase() === opt.label?.toLowerCase())
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                  {opt.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default SearchableCombobox;
